import { importItemsActions } from '@common-reducers/ImportItemsReducer'
import { useAppDispatch } from '@common-reducers/hooks/store.hook'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { loadXlsx } from '@extension/utils/lazy-lib'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'
import { namespace } from '@common/config/constants'
import { parseImportedFile, importErrorKey, XlsxLike } from './parse-imported-file'

function Dropzone() {
    const { t } = useTranslation(namespace);
    const [error, setError] = useState<string | undefined>(undefined);
    const dispatch = useAppDispatch()

    const acceptedExtensionList = [
        '.csv',
        '.xlsx',
        '.xls'
    ];

    const onDrop = useCallback((acceptedFiles: File[]) => {
        const file = acceptedFiles[0]
        if (!file) return;

        const [extension] = file.name.split('.').reverse()

        if (!acceptedExtensionList.includes(`.${(extension ?? '').toLowerCase()}`)) {
            setError(t('importExcelToBlueticks.invalidFileExtension', {
                extension: extension ?? '',
                formats: acceptedExtensionList.join(', ')
            }))
            return
        }

        const fileReader = new FileReader()
        fileReader.onload = async () => {
            try {
                const Xlsx = await loadXlsx()
                // FE#239: parse via the shared, unit-tested helper so phone/ID
                // columns keep their exact text (leading zeros, a leading +) and a
                // file that yields zero rows throws instead of silently registering
                // as "uploaded" and stranding the flow at a 0-contact audience.
                const { sheetName, rows } = parseImportedFile(fileReader.result, Xlsx as unknown as XlsxLike)
                dispatch(importItemsActions.setImportProps({
                    importFileName: file.name,
                    workSheetName: sheetName,
                    importJsonData: rows,
                    file
                }))
            } catch (error) {
                console.error(error)
                const key = importErrorKey(error)
                if (key === 'noRowsInFile') {
                    setError(t('importExcelToBlueticks.noRowsInFile', {
                        defaultValue: 'No contacts found in this file. Check that the first row is a header (e.g. Name, Phone) with contact rows below it, then try again.'
                    }))
                } else if (key === 'invalidFileContent') {
                    setError(t('importExcelToBlueticks.invalidFileContent', {
                        defaultValue: "This file isn't a valid CSV/Excel file. Download the sample and try again."
                    }))
                } else {
                    setError(t('importExcelToBlueticks.somethingWentWrong'))
                }
            }
        }

        fileReader.readAsArrayBuffer(file)

    }, [dispatch])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop })
    
    return (
        <>
            <div  
                {...getRootProps()} 
                className={cn(
                    "h-[25vh] border-2 border-dashed border-[var(--tasks-input-border)] dark:border-[var(--tasks-input-border)]",
                    "flex justify-center items-center rounded-[5px] cursor-pointer",
                    "hover:border-[var(--color-accent)] transition-colors"
                )}
            >
                <input {...getInputProps()} accept="text/csv application/vnd.openxmlformats-officedocument.spreadsheetml.sheet application/vnd.ms-excel .csv .xlsx .xls" />
                <div className="text-center">
                    <p className="text-xl text-center text-[var(--color-text)] dark:text-[var(--color-text)]">
                        {t('importExcelToBlueticks.dropFilesHereOr')}&nbsp;
                        <span className="text-[var(--color-accent)] dark:text-[var(--color-accent)]">
                            {t('importExcelToBlueticks.browseYourDevice')}
                        </span>
                    </p>
                    <p className="text-sm text-center text-[var(--color-text-muted)] dark:text-[var(--color-text-muted)] mt-2">
                        {acceptedExtensionList.join(', ')}
                    </p>
                </div>
            </div>
            {error && (
                <div 
                    className={cn(
                        "fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50",
                        "bg-red-600 text-white px-4 py-2 rounded-md shadow-lg",
                        "animate-in fade-in slide-in-from-bottom-4"
                    )}
                    onClick={() => setError(undefined)}
                >
                    {error}
                </div>
            )}
        </>
    )
}

export default Dropzone
